<script lang="ts" setup>
import { FontAwesomeIcon } from '@shared/plugin/icons';
import { useMediaQuery } from '@vueuse/core';
import { useSlots } from 'vue';
import { useScrollLock } from '@shared/layout/useScrollLock';

export interface Props {
  open?: boolean;
  preventClose?: boolean;
  width?: number;
}

const props = withDefaults(defineProps<Props>(), {
  open: false,
  preventClose: false,
  width: 800,
});

const emit = defineEmits<{ close: [] }>();

useScrollLock(() => props.open);

const slots = useSlots();
const isLargeScreen = useMediaQuery(`(min-width: 900px)`);
</script>

<template>
  <div
    :class="[
      'side-panel fixed top-0 z-201 flex h-full w-full flex-col overflow-auto bg-white transition-[right] duration-300 ease-in-out md:w-[75vw] md:max-w-[960px]',
      props.open ? 'right-0' : '-right-full',
    ]"
    data-testid="side-panel"
    :style="{
      maxWidth: width && isLargeScreen ? `${width}px` : '100%',
    }"
    v-bind="$attrs"
  >
    <div class="sticky top-0 z-20 flex h-16 shrink-0 items-center bg-inherit">
      <button
        class="h-full cursor-pointer border-none bg-transparent px-4 text-[1.5rem] outline-none"
        :disabled="props.preventClose"
        @click.prevent="props.preventClose ? null : emit('close')"
        type="button"
      >
        <FontAwesomeIcon icon="fa-regular fa-times" />
      </button>
      <span
        v-if="$slots.header"
        class="flex h-full flex-1 items-center bg-primary-600 em-pl-8 text-white"
      >
        <slot name="header"></slot>
      </span>
    </div>
    <div class="mt-8 flex grow flex-col justify-between bg-inherit">
      <div class="w-full grow p-[0.5em] px-8">
        <slot></slot>
      </div>
      <div
        v-show="slots.footer"
        class="sticky bottom-0 z-dialog flex items-center justify-end border-t border-solid border-t-grey-200 bg-inherit px-8 py-4"
      >
        <slot name="footer"></slot>
      </div>
    </div>
  </div>

  <div
    :class="[
      'fixed inset-0 z-200 bg-black/80 transition-opacity duration-300',
      {
        'pointer-events-none opacity-0': !props.open,
      },
    ]"
    @click.prevent="props.preventClose ? null : emit('close')"
  ></div>
</template>
